/*
 * Panelden (Mesajlar → Yeni Mesaj) "Pop up" ya da "Notification" türüyle
 * gönderilen mesajlar: pop up ortada bir pencere, notification kısa bir
 * bildirim olarak gösterilir ve gösterildiği anda okundu işaretlenir.
 * "Internal Message" yalnızca gelen kutusundadır, burada çizilmez.
 *
 * Yoklama yalnızca giriş yapmış üyede ve seyrektir (POLL_MS): uç tek bir
 * indeksli sorgudur, sohbet rayındaki gibi her saniye sorulmaz.
 *
 * Etki üyenin **kimliğine** bağlıdır, kullanıcı nesnesine değil: bakiye her
 * spinde değişir ve nesne yenilenir. Eskiden her spinde yoklama baştan
 * kuruluyor ve hemen bir istek atılıyordu (BETRUN, 25.10.2026: oyundaki bir
 * telefon 1-2 sn'de bir; `/messages/live` bütün API trafiğinin %32'si).
 */
import React, { useCallback, useEffect, useState } from 'react';
import { X } from 'lucide-react';
import client from '../lib/api';
import realtime, { pollGate } from '../lib/realtime';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../hooks/use-toast';

export const POLL_MS = 60000;

const PlayerMessageAlerts = () => {
  const { user, ready } = useAuth() || {};
  const userId = user?.id || '';
  const { toast } = useToast();
  const [popup, setPopup] = useState(null);
  const [queue, setQueue] = useState([]);

  const markRead = useCallback((id) => client.patch(`/messages/${id}/read`).catch(() => {}), []);

  const poll = useCallback(async () => {
    try {
      const { data } = await client.get('/messages/live');
      const items = data?.items || [];
      items.filter((m) => m.type === 'notification').forEach((m) => {
        toast({ title: m.subject, description: m.body });
        markRead(m.id);
      });
      const popups = items.filter((m) => m.type === 'popup');
      if (popups.length) setQueue((current) => [...current, ...popups.filter((p) => !current.some((c) => c.id === p.id))]);
    } catch (error) { /* oturum düşmüş olabilir; sessiz */ }
  }, [markRead, toast]);

  useEffect(() => {
    if (!ready || !userId) return undefined;
    poll();
    // Kanal canlıyken yeni mesaj `messages` olayıyla gelir; yoklama 5 dk'ya iner.
    const gate = pollGate(300000);
    const timer = window.setInterval(() => { if (!gate.skip()) poll(); }, POLL_MS);
    const off = realtime.on('messages', (data) => { gate.touch(); if (data?.unread) poll(); });
    return () => { window.clearInterval(timer); off(); };
  }, [ready, userId, poll]);

  useEffect(() => {
    if (popup || !queue.length) return;
    const [next, ...rest] = queue;
    setPopup(next);
    setQueue(rest);
    markRead(next.id);
  }, [popup, queue, markRead]);

  if (!popup) return null;
  return (
    <div className="player-msg-layer" role="dialog" aria-modal="true" aria-label={popup.subject}>
      <div className="player-msg-box">
        <button type="button" className="player-msg-close" aria-label="Kapat" onClick={() => setPopup(null)}><X size={18} /></button>
        <h3>{popup.subject}</h3>
        <p>{popup.body}</p>
        <button type="button" className="player-msg-ok" onClick={() => setPopup(null)}>Tamam</button>
      </div>
    </div>
  );
};

export default PlayerMessageAlerts;
